import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, FileText, MessageSquare, Flag, Star, Users, Settings, Eye, Trash2, EyeOff, Plus, Instagram, Facebook, Linkedin, Youtube, Twitter, AlertTriangle, History, Mail, UserCheck, Reply, Check, Building, X, Pencil, BookOpen, Target } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';
import StarRating from '../components/StarRating';

const API = process.env.REACT_APP_BACKEND_URL;

const KURSO_ROLES = [
  { value: 'standard',   label: 'Utilisateur standard' },
  { value: 'editeur',    label: 'Éditeur Kurso' },
  { value: 'moderateur', label: 'Modérateur Kurso' },
  { value: 'analyste',   label: 'Analyste' },
  { value: 'superadmin', label: 'Super Admin' },
];

const LOGIN_METHOD_LABELS = {
  email: 'Email',
  google: 'Google',
  microsoft: 'Microsoft',
  facebook: 'Facebook',
};

function timeAgo(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Aujourd'hui";
  return `Il y a ${days} j`;
}

const TABS = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: Shield },
  { id: 'messages', label: 'Messages', icon: Mail },
  { id: 'reviews', label: 'Avis signalés', icon: Star },
  { id: 'articles', label: 'Articles', icon: FileText },
  { id: 'forum', label: 'Forum', icon: MessageSquare },
  { id: 'users', label: 'Utilisateurs', icon: Users },
  { id: 'mentors_admin', label: 'Mentors', icon: UserCheck },
  { id: 'suspects', label: 'Suspects', icon: AlertTriangle },
  { id: 'espaces', label: 'Espaces', icon: Building },
  { id: 'referentials', label: 'Référentiels', icon: BookOpen },
  { id: 'costs', label: 'Coûts IA', icon: History },
  { id: 'audit', label: 'Journal', icon: History },
  { id: 'social', label: 'Paramètres', icon: Settings },
];

export default function SuperAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [socialForm, setSocialForm] = useState({ facebook: '', instagram: '', linkedin: '', tiktok: '', youtube: '', twitter: '' });
  const [crispId, setCrispId] = useState('');
  const [replyModal, setReplyModal] = useState(null); // {message}
  const [replyText, setReplyText] = useState('');
  // Super Admin confirmation modal
  const [superAdminModal, setSuperAdminModal] = useState(null); // {userId, currentRole}
  // Spaces state
  const [createSpaceModal, setCreateSpaceModal] = useState(false);
  const [editSpaceModal, setEditSpaceModal] = useState(null); // space object
  const [spaceForm, setSpaceForm] = useState({ name: '', type: 'organisation', description: '', owner_email: '' });

  // Protect route
  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/dashboard');
  }, [user, navigate]);

  const fetchTabData = useCallback(async (tab) => {
    setLoading(true);
    try {
      if (tab === 'overview') {
        const [ratingStats, forumQueue] = await Promise.all([
          axios.get(`${API}/api/ratings/admin/stats`, { withCredentials: true }),
          axios.get(`${API}/api/forum/admin/queue`, { withCredentials: true }),
        ]);
        setData({ ratingStats: ratingStats.data, forumQueue: forumQueue.data });
      } else if (tab === 'reviews') {
        const r = await axios.get(`${API}/api/ratings/admin/flagged`, { withCredentials: true });
        setData({ flaggedReviews: r.data.reviews });
      } else if (tab === 'articles') {
        const r = await axios.get(`${API}/api/articles/admin/all`, { withCredentials: true });
        setData({ articles: r.data.articles });
      } else if (tab === 'forum') {
        const r = await axios.get(`${API}/api/forum/admin/queue`, { withCredentials: true });
        setData({ forumQueue: r.data.flagged });
      } else if (tab === 'users') {
        const r = await axios.get(`${API}/api/social/admin/users`, { withCredentials: true });
        setData({ users: r.data.users });
      } else if (tab === 'suspects') {
        const r = await axios.get(`${API}/api/usage/admin/flagged`, { withCredentials: true });
        setData({ flags: r.data.flags || [] });
      } else if (tab === 'espaces') {
        const r = await axios.get(`${API}/api/spaces/admin/list`, { withCredentials: true });
        setData({ spaces: r.data || [] });
      } else if (tab === 'referentials') {
        const r = await axios.get(`${API}/api/referentials`, { withCredentials: true });
        setData({ referentials: r.data.referentials || [] });
      } else if (tab === 'costs') {
        const r = await axios.get(`${API}/api/usage/admin/summary`, { withCredentials: true });
        setData({ costs: r.data });
      } else if (tab === 'audit') {
        const r = await axios.get(`${API}/api/superadmin/audit-log`, { withCredentials: true });
        setData({ auditLog: r.data.entries || [] });
      } else if (tab === 'messages') {
        const r = await axios.get(`${API}/api/contact/messages`, { withCredentials: true });
        setData({ messages: r.data.messages || [] });
      } else if (tab === 'mentors_admin') {
        const r = await axios.get(`${API}/api/mentors/admin/list`, { withCredentials: true });
        setData({ mentors: r.data.mentors || [] });
      } else if (tab === 'social') {
        const [socialRes] = await Promise.all([
          axios.get(`${API}/api/social/settings`),
        ]);
        setSocialForm(socialRes.data);
        try {
          const crispRes = await axios.get(`${API}/api/contact/settings`, { withCredentials: true });
          setCrispId(crispRes.data.crisp_website_id || '');
        } catch { /* ignore if no access */ }
        setData({ social: socialRes.data });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user?.role === 'admin') fetchTabData(activeTab); }, [activeTab, user, fetchTabData]);

  const handleModerateReview = async (reviewId, action) => {
    await axios.put(`${API}/api/ratings/admin/review/${reviewId}/moderate`, null, { params: { action }, withCredentials: true });
    toast.success(`Action : ${action}`);
    fetchTabData('reviews');
  };

  const handleModeratePost = async (postId, action) => {
    await axios.put(`${API}/api/forum/admin/post/${postId}/moderate`, null, { params: { action }, withCredentials: true });
    toast.success(`Action : ${action}`);
    fetchTabData('forum');
  };

  const handlePinArticle = async (articleId) => {
    await axios.post(`${API}/api/articles/${articleId}/pin`, {}, { withCredentials: true });
    toast.success('Article épinglé');
    fetchTabData('articles');
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    await axios.delete(`${API}/api/articles/${articleId}`, { withCredentials: true });
    toast.success('Article supprimé');
    fetchTabData('articles');
  };

  const handleUserRole = async (userId, role) => {
    await axios.put(`${API}/api/social/admin/users/${userId}/role`, null, { params: { role }, withCredentials: true });
    toast.success(`Rôle mis à jour : ${role}`);
    fetchTabData('users');
  };

  const handleCommunityRole = async (userId, role) => {
    await axios.put(`${API}/api/forum/admin/user/${userId}/community-role`, null, { params: { role }, withCredentials: true });
    toast.success(`Rôle communautaire : ${role}`);
    fetchTabData('users');
  };

  const handleKursoRole = async (userId, newRole) => {
    if (newRole === 'superadmin') {
      setSuperAdminModal({ userId });      return;
    }
    try {
      await axios.put(`${API}/api/superadmin/users/${userId}/kurso-role`, { kurso_role: newRole }, { withCredentials: true });
      toast.success(`Rôle équipe mis à jour : ${KURSO_ROLES.find(r => r.value === newRole)?.label}`);
      fetchTabData('users');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du changement de rôle');
    }
  };

  const confirmSuperAdmin = async () => {
    if (!superAdminModal) return;
    try {
      await axios.put(`${API}/api/superadmin/users/${superAdminModal.userId}/kurso-role`, { kurso_role: 'superadmin' }, { withCredentials: true });
      toast.success('Rôle Super Admin attribué');
      setSuperAdminModal(null);
      fetchTabData('users');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
      setSuperAdminModal(null);
    }
  };

  const handleSaveSocial = async () => {
    await axios.put(`${API}/api/social/settings`, socialForm, { withCredentials: true });
    if (crispId !== undefined) {
      try {
        await axios.put(`${API}/api/contact/settings`, { crisp_website_id: crispId }, { withCredentials: true });
      } catch { /* ignore */ }
    }
    toast.success('Paramètres sauvegardés');
  };

  const handleUpdateMessageStatus = async (msgId, status) => {
    try {
      await axios.put(`${API}/api/contact/messages/${msgId}/status`, { status }, { withCredentials: true });
      toast.success('Statut mis à jour');
      fetchTabData('messages');
    } catch { toast.error('Erreur'); }
  };

  const handleSendReply = async () => {
    if (!replyModal || !replyText.trim()) return;
    try {
      await axios.post(`${API}/api/contact/messages/${replyModal.id}/reply`,
        { reply: replyText }, { withCredentials: true });
      toast.success('Réponse envoyée');
      setReplyModal(null);
      setReplyText('');
      fetchTabData('messages');
    } catch { toast.error('Erreur lors de l\'envoi'); }
  };

  const handleFlagAction = async (userId, action) => {
    try {
      await axios.post(`${API}/api/usage/admin/flag-action`, { user_id: userId, action }, { withCredentials: true });
      toast.success(`Action "${action}" effectuée`);
      fetchTabData('suspects');
    } catch { toast.error('Erreur lors de l\'action'); }
  };

  // ---- SPACE HANDLERS ----
  const handleCreateSpace = async () => {
    if (!spaceForm.name.trim()) { toast.error('Le nom est requis'); return; }
    try {
      await axios.post(`${API}/api/spaces`, spaceForm, { withCredentials: true });
      toast.success('Espace créé');
      setCreateSpaceModal(false);
      setSpaceForm({ name: '', type: 'organisation', description: '', owner_email: '' });
      fetchTabData('espaces');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdateSpace = async () => {
    if (!editSpaceModal) return;
    try {
      await axios.put(`${API}/api/spaces/${editSpaceModal.id}`,
        { name: editSpaceModal.name, description: editSpaceModal.description, status: editSpaceModal.status },
        { withCredentials: true });
      toast.success('Espace mis à jour');
      setEditSpaceModal(null);
      fetchTabData('espaces');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteSpace = async (spaceId) => {
    if (!window.confirm('Supprimer cet espace et désactiver tous ses membres ?')) return;
    try {
      await axios.delete(`${API}/api/spaces/${spaceId}`, { withCredentials: true });
      toast.success('Espace supprimé');
      fetchTabData('espaces');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleToggleMentorFeature = async (mentorId) => {
    try {
      await axios.put(`${API}/api/mentors/admin/${mentorId}/feature`, {}, { withCredentials: true });
      toast.success('Statut mis en avant mis à jour');
      fetchTabData('mentors_admin');
    } catch { toast.error('Erreur'); }
  };

  const handleToggleMentorStatus = async (mentorId) => {
    try {
      await axios.put(`${API}/api/mentors/admin/${mentorId}/status`, {}, { withCredentials: true });
      toast.success('Statut mentor mis à jour');
      fetchTabData('mentors_admin');
    } catch { toast.error('Erreur'); }
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-[#0f0e17]">
      {/* Super Admin confirmation modal */}
      {superAdminModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-[#1a1a2e] border border-[#ff6b6b]/40 rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={20} className="text-[#ff6b6b]" />
              <h3 className="text-white font-bold">Confirmer Super Admin</h3>
            </div>
            <p className="text-[#a0a0ab] text-sm mb-6">
              Êtes-vous sûr ? Le Super Admin a accès à <strong className="text-white">toutes les données de la plateforme</strong>. Maximum 2 Super Admins autorisés.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setSuperAdminModal(null)} className="flex-1 py-2 rounded-xl border border-white/10 text-[#a0a0ab] text-sm hover:bg-white/5">Annuler</button>
              <button onClick={confirmSuperAdmin} data-testid="confirm-superadmin-btn" className="flex-1 py-2 rounded-xl bg-[#ff6b6b] text-white text-sm font-bold hover:bg-[#ff5555]">Confirmer</button>
            </div>
          </div>
        </div>
      )}
      <header className="sticky top-0 z-40 bg-[#0f0e17]/90 backdrop-blur-xl border-b border-white/5 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield size={18} className="text-[#ff6b6b]" />
            <span className="font-heading font-bold text-white">Super Admin</span>
          </div>
          <button onClick={() => navigate('/dashboard')} className="text-sm text-[#a0a0ab] hover:text-white">← Dashboard</button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1 mb-6 scrollbar-hide">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id}
                onClick={() => setSearchParams({ tab: tab.id })}
                data-testid={`admin-tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium flex-shrink-0 transition-colors ${activeTab === tab.id ? 'bg-[#ff6b6b] text-white' : 'bg-white/5 text-[#a0a0ab] hover:text-white hover:bg-white/10'}`}>
                <Icon size={12} /> {tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-2 border-[#ff6b6b] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <>
            {/* MESSAGES */}
            {activeTab === 'messages' && (
              <div className="space-y-3">
                <h2 className="text-lg font-heading font-bold text-white">Messages de contact ({data.messages?.length || 0})</h2>
                {!data.messages?.length && <p className="text-[#a0a0ab] text-sm">Aucun message reçu.</p>}
                {(data.messages || []).map(msg => (
                  <div key={msg.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <span className="text-white font-medium">{msg.name}</span>
                        <span className="text-xs text-[#a0a0ab] ml-2">{msg.email}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${msg.status === 'Nouveau' ? 'bg-[#ff6b6b]/20 text-[#ff6b6b]' : msg.status === 'Résolu' ? 'bg-[#06d6a0]/20 text-[#06d6a0]' : 'bg-[#ffd93d]/20 text-[#ffd93d]'}`}>
                        {msg.status}
                      </span>
                    </div>
                    <p className="text-xs font-medium text-[#ff6b6b]">{msg.subject || msg.category}</p>
                    <p className="text-[#e8e8e8] text-sm">{msg.message?.substring(0, 200)}{msg.message?.length > 200 ? '...' : ''}</p>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => { setReplyModal(msg); setReplyText(''); }}
                        className="flex items-center gap-1 text-xs bg-[#20c997]/10 text-[#20c997] px-2 py-1 rounded-lg hover:bg-[#20c997]/20 transition-colors">
                        <Reply size={10} /> Répondre
                      </button>
                      <button onClick={() => handleUpdateMessageStatus(msg.id, 'En cours')}
                        className="text-xs bg-[#ffd93d]/10 text-[#ffd93d] px-2 py-1 rounded-lg hover:bg-[#ffd93d]/20">
                        En cours
                      </button>
                      <button onClick={() => handleUpdateMessageStatus(msg.id, 'Résolu')}
                        className="flex items-center gap-1 text-xs bg-[#06d6a0]/10 text-[#06d6a0] px-2 py-1 rounded-lg hover:bg-[#06d6a0]/20">
                        <Check size={10} /> Résolu
                      </button>
                    </div>
                    <p className="text-xs text-[#a0a0ab]">{new Date(msg.created_at).toLocaleString('fr-FR')}</p>
                  </div>
                ))}
                {/* Reply modal */}
                {replyModal && (
                  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
                    <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 max-w-md w-full">
                      <h3 className="text-white font-bold mb-1">Répondre à {replyModal.name}</h3>
                      <p className="text-xs text-[#a0a0ab] mb-3">{replyModal.email}</p>
                      <textarea
                        value={replyText} onChange={e => setReplyText(e.target.value)}
                        rows={5} placeholder="Votre réponse..."
                        className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-xl p-3 focus:outline-none focus:border-[#ff6b6b]/50 resize-none mb-3"
                      />
                      <div className="flex gap-2">
                        <button onClick={() => setReplyModal(null)} className="flex-1 py-2 rounded-xl border border-white/10 text-[#a0a0ab] text-sm">Annuler</button>
                        <button onClick={handleSendReply} className="flex-1 py-2 rounded-xl bg-[#ff6b6b] text-white text-sm font-bold">Envoyer</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* MENTORS ADMIN */}
            {activeTab === 'mentors_admin' && (
              <div className="space-y-3">
                <h2 className="text-lg font-heading font-bold text-white">Mentors ({data.mentors?.length || 0})</h2>
                {!data.mentors?.length && <p className="text-[#a0a0ab] text-sm">Aucun mentor inscrit.</p>}
                {(data.mentors || []).map(m => (
                  <div key={m.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-3 flex-wrap">
                    <div className="w-9 h-9 rounded-full bg-[#20c997]/20 flex items-center justify-center text-[#20c997] text-sm font-bold flex-shrink-0">
                      {m.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-medium">{m.name}</p>
                        {m.is_featured && <span className="text-xs text-[#ffd93d]">⭐ Mis en avant</span>}
                        {!m.is_public && <span className="text-xs bg-white/10 text-[#a0a0ab] px-1.5 py-0.5 rounded">Masqué</span>}
                        {!m.charter_accepted && <span className="text-xs bg-[#ff6b6b]/20 text-[#ff6b6b] px-1.5 py-0.5 rounded">Charte non signée</span>}
                      </div>
                      <p className="text-xs text-[#a0a0ab]">{m.email} · {m.avg_rating?.toFixed(1) || 0}⭐ · {m.total_sessions || 0} sessions</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleToggleMentorFeature(m.id)}
                        className="text-xs bg-[#ffd93d]/10 text-[#ffd93d] px-2 py-1.5 rounded-lg hover:bg-[#ffd93d]/20">
                        {m.is_featured ? 'Retirer vedette' : 'Mettre en vedette'}
                      </button>
                      <button onClick={() => handleToggleMentorStatus(m.id)}
                        className={`text-xs px-2 py-1.5 rounded-lg ${m.is_active !== false ? 'bg-[#ff6b6b]/10 text-[#ff6b6b] hover:bg-[#ff6b6b]/20' : 'bg-[#06d6a0]/10 text-[#06d6a0] hover:bg-[#06d6a0]/20'}`}>
                        {m.is_active !== false ? 'Désactiver' : 'Réactiver'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* UTILISATEURS SUSPECTS */}
            {activeTab === 'suspects' && (
              <div className="space-y-3">
                <div>
                  <h2 className="text-lg font-heading font-bold text-white">Utilisateurs suspects ({data.flags?.length || 0})</h2>
                  <p className="text-xs text-[#a0a0ab] mt-1">Utilisateurs automatiquement signalés pour utilisation abusive. Actions disponibles : avertir, suspendre, upgrade Créateur Pro, bannir.</p>
                </div>
                {!data.flags?.length && <div className="kurso-card p-6 text-center"><p className="text-[#06d6a0] text-sm">Aucun utilisateur suspect détecté.</p></div>}
                {(data.flags || []).map(flag => (
                  <div key={flag.user_id} className="bg-[#ff6b6b]/5 border border-[#ff6b6b]/20 rounded-xl p-4 space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <span className="text-white font-medium">{flag.user_name}</span>
                        <span className="text-xs text-[#a0a0ab] ml-2">{flag.user_email}</span>
                        <span className="text-xs ml-2 bg-white/10 text-[#e8e8e8] px-1.5 py-0.5 rounded capitalize">{flag.plan}</span>
                      </div>
                      <span className="text-xs text-[#a0a0ab]">{new Date(flag.created_at).toLocaleString('fr-FR')}</span>
                    </div>
                    <ul className="space-y-0.5">
                      {(flag.flags || []).map((f, i) => (
                        <li key={f.detail || `flag-${i}`} className="text-xs text-[#ffc107] flex items-center gap-1.5">
                          <AlertTriangle size={10} className="flex-shrink-0" /> {f.detail}
                        </li>
                      ))}
                    </ul>
                    <div className="flex gap-2 flex-wrap pt-1">
                      <button onClick={() => handleFlagAction(flag.user_id, 'warn')} className="text-xs bg-[#ffc107]/10 text-[#ffc107] px-2 py-1.5 rounded-lg hover:bg-[#ffc107]/20">
                        Avertir par email
                      </button>
                      <button onClick={() => handleFlagAction(flag.user_id, 'upgrade')} className="text-xs bg-[#20c997]/10 text-[#20c997] px-2 py-1.5 rounded-lg hover:bg-[#20c997]/20">
                        Upgrade Créateur Pro
                      </button>
                      <button onClick={() => handleFlagAction(flag.user_id, 'suspend')} className="text-xs bg-[#ff6b6b]/10 text-[#ff6b6b] px-2 py-1.5 rounded-lg hover:bg-[#ff6b6b]/20">
                        Suspendre
                      </button>
                      <button onClick={() => handleFlagAction(flag.user_id, 'ban')} className="text-xs bg-black/30 text-[#a0a0ab] px-2 py-1.5 rounded-lg hover:bg-black/50">
                        Bannir définitivement
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ESPACES */}
            {activeTab === 'espaces' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-heading font-bold text-white">Espaces ({data.spaces?.length || 0})</h2>
                    <p className="text-xs text-[#a0a0ab] mt-0.5">Gérez les espaces famille, organisation et entreprise de la plateforme.</p>
                  </div>
                  <button
                    onClick={() => { setSpaceForm({ name: '', type: 'organisation', description: '', owner_email: '' }); setCreateSpaceModal(true); }}
                    className="btn-primary flex items-center gap-1.5 text-sm py-2 px-3"
                    data-testid="create-space-btn"
                  >
                    <Plus size={12} /> Créer un espace
                  </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  {['famille', 'organisation', 'entreprise'].map(type => {
                    const count = (data.spaces || []).filter(s => s.type === type).length;
                    const colors = { famille: '#20c997', organisation: '#a78bfa', entreprise: '#ffc107' };
                    return (
                      <div key={type} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                        <p className="text-xl font-bold" style={{ color: colors[type] }}>{count}</p>
                        <p className="text-xs text-[#a0a0ab] capitalize mt-0.5">{type}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Table */}
                {!(data.spaces?.length) && <p className="text-[#a0a0ab] text-sm">Aucun espace créé. Utilisez le bouton ci-dessus pour en créer un.</p>}
                <div className="space-y-2">
                  {(data.spaces || []).map(space => {
                    const typeColors = { famille: 'text-[#20c997] bg-[#20c997]/10 border-[#20c997]/20', organisation: 'text-[#a78bfa] bg-[#a78bfa]/10 border-[#a78bfa]/20', entreprise: 'text-[#ffc107] bg-[#ffc107]/10 border-[#ffc107]/20' };
                    return (
                      <div key={space.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#ff6b6b]/10 flex items-center justify-center flex-shrink-0">
                          <Building size={16} className="text-[#ff6b6b]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-white text-sm font-medium">{space.name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full border capitalize font-medium ${typeColors[space.type] || 'text-[#a0a0ab] bg-white/5 border-white/10'}`}>{space.type}</span>
                            {space.status === 'suspended' && <span className="text-xs text-[#ff6b6b] bg-[#ff6b6b]/10 border border-[#ff6b6b]/20 px-2 py-0.5 rounded-full">Suspendu</span>}
                          </div>
                          <p className="text-xs text-[#a0a0ab] truncate">{space.owner_email} · {space.member_count} membre{space.member_count !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={() => setEditSpaceModal({ ...space })}
                            data-testid={`edit-space-${space.id}`}
                            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                            title="Modifier"
                          >
                            <Pencil size={12} className="text-[#e8e8e8]" />
                          </button>
                          <button
                            onClick={() => handleDeleteSpace(space.id)}
                            data-testid={`delete-space-${space.id}`}
                            className="p-1.5 rounded-lg bg-[#ff6b6b]/10 hover:bg-[#ff6b6b]/20 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 size={12} className="text-[#ff6b6b]" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Create Space Modal */}
                {createSpaceModal && (
                  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
                    <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 max-w-md w-full">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-white font-bold text-lg">Créer un espace</h3>
                        <button onClick={() => setCreateSpaceModal(false)} className="text-[#a0a0ab] hover:text-white"><X size={18} /></button>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-[#a0a0ab] mb-1 block">Nom *</label>
                          <input value={spaceForm.name} onChange={e => setSpaceForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="Mon Organisation"
                            className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                            data-testid="space-name-input"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-[#a0a0ab] mb-1 block">Type</label>
                          <select value={spaceForm.type} onChange={e => setSpaceForm(f => ({ ...f, type: e.target.value }))}
                            className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none"
                            data-testid="space-type-select"
                          >
                            <option value="famille">Famille</option>
                            <option value="organisation">Organisation</option>
                            <option value="entreprise">Entreprise</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-[#a0a0ab] mb-1 block">Description (optionnel)</label>
                          <textarea value={spaceForm.description} onChange={e => setSpaceForm(f => ({ ...f, description: e.target.value }))}
                            rows={2} placeholder="Description de l'espace..."
                            className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none resize-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-[#a0a0ab] mb-1 block">Email du propriétaire (optionnel — défaut : vous-même)</label>
                          <input value={spaceForm.owner_email} onChange={e => setSpaceForm(f => ({ ...f, owner_email: e.target.value }))}
                            placeholder="user@example.com"
                            type="email"
                            className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none"
                            data-testid="space-owner-email-input"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => setCreateSpaceModal(false)} className="flex-1 py-2.5 rounded-xl border border-white/10 text-[#a0a0ab] text-sm hover:bg-white/5">Annuler</button>
                        <button onClick={handleCreateSpace} data-testid="confirm-create-space-btn" className="flex-1 py-2.5 rounded-xl bg-[#ff6b6b] text-white text-sm font-bold hover:bg-[#ff5555]">Créer l'espace</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Edit Space Modal */}
                {editSpaceModal && (
                  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
                    <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 max-w-md w-full">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-white font-bold text-lg">Modifier l'espace</h3>
                        <button onClick={() => setEditSpaceModal(null)} className="text-[#a0a0ab] hover:text-white"><X size={18} /></button>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-[#a0a0ab] mb-1 block">Nom</label>
                          <input value={editSpaceModal.name || ''} onChange={e => setEditSpaceModal(s => ({ ...s, name: e.target.value }))}
                            className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                            data-testid="edit-space-name-input"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-[#a0a0ab] mb-1 block">Description</label>
                          <textarea value={editSpaceModal.description || ''} onChange={e => setEditSpaceModal(s => ({ ...s, description: e.target.value }))}
                            rows={2}
                            className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none resize-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-[#a0a0ab] mb-1 block">Statut</label>
                          <select value={editSpaceModal.status || 'active'} onChange={e => setEditSpaceModal(s => ({ ...s, status: e.target.value }))}
                            className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none"
                          >
                            <option value="active">Actif</option>
                            <option value="suspended">Suspendu</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => setEditSpaceModal(null)} className="flex-1 py-2.5 rounded-xl border border-white/10 text-[#a0a0ab] text-sm hover:bg-white/5">Annuler</button>
                        <button onClick={handleUpdateSpace} data-testid="confirm-edit-space-btn" className="flex-1 py-2.5 rounded-xl bg-[#ff6b6b] text-white text-sm font-bold hover:bg-[#ff5555]">Sauvegarder</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* RÉFÉRENTIELS */}
            {activeTab === 'referentials' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-heading font-bold text-white">Référentiels ({data.referentials?.length || 0})</h2>
                  <p className="text-xs text-[#a0a0ab] mt-0.5">
                    Cadres de compétences officiels pré-chargés dans la plateforme.
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(data.referentials || []).map(ref => (
                    <div
                      key={ref.id || ref.name}
                      data-testid={`ref-admin-${ref.name}`}
                      className="bg-white/5 border border-white/10 rounded-xl p-4"
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <div className="w-7 h-7 rounded-lg bg-[#ff6b6b]/10 flex items-center justify-center flex-shrink-0">
                          <Target size={13} className="text-[#ff6b6b]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-semibold leading-tight">{ref.name}</p>
                          <p className="text-xs text-[#a0a0ab]">v{ref.version}</p>
                        </div>
                      </div>
                      <p className="text-xs text-[#a0a0ab] leading-relaxed line-clamp-2">{ref.organization}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs bg-[#20c997]/10 text-[#20c997] px-2 py-0.5 rounded-full border border-[#20c997]/20">
                          {ref.visibility || 'public'}
                        </span>
                        {ref.official_url && (
                          <a
                            href={ref.official_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#a0a0ab] hover:text-white transition-colors"
                            title="Site officiel"
                          >
                            ↗
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-[#ffc107]/5 border border-[#ffc107]/20 rounded-xl p-4">
                  <p className="text-[#ffc107] text-xs font-medium mb-1">Note pour les administrateurs</p>
                  <p className="text-[#a0a0ab] text-xs leading-relaxed">
                    Les référentiels sont mis à jour automatiquement à chaque démarrage du serveur (upsert idempotent). Pour ajouter un nouveau référentiel, modifiez la liste <code className="text-white bg-white/10 px-1 rounded">DEFAULT_REFERENTIALS</code> dans <code className="text-white bg-white/10 px-1 rounded">referentials.py</code>.
                  </p>
                </div>
              </div>
            )}

            {/* COÛTS IA */}
            {activeTab === 'costs' && (
              <div className="space-y-4">
                <h2 className="text-lg font-heading font-bold text-white">Tableau des coûts IA</h2>
                {data.costs && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="kurso-card p-4">
                        <p className="text-xs text-[#a0a0ab] mb-1">Coût total ce mois</p>
                        <p className="text-2xl font-bold text-white">{data.costs.total_cost_eur?.toFixed(4)} €</p>
                      </div>
                      <div className="kurso-card p-4">
                        <p className="text-xs text-[#a0a0ab] mb-1">Générations totales</p>
                        <p className="text-2xl font-bold text-white">{data.costs.total_generations}</p>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-sm mb-2">Par plan</h3>
                      <div className="space-y-1.5">
                        {(data.costs.cost_by_plan || []).map(p => (
                          <div key={p.plan} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                            <span className="text-sm text-white capitalize">{p.plan}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-[#a0a0ab]">{p.count} générations</span>
                              <span className="text-sm font-medium text-white">{p.cost?.toFixed(4)} €</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-sm mb-2">Top 10 utilisateurs par coût</h3>
                      <div className="space-y-1.5">
                        {(data.costs.top_users || []).map((u, i) => (
                          <div key={u.user_id} className={`flex items-center justify-between rounded-lg px-3 py-2 ${u.exceeds_threshold ? 'bg-[#ff6b6b]/10 border border-[#ff6b6b]/20' : 'bg-white/5'}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-xs text-[#a0a0ab] w-4">{i + 1}.</span>
                              <div className="min-w-0">
                                <p className="text-sm text-white truncate">{u.name}</p>
                                <p className="text-xs text-[#a0a0ab] truncate">{u.email} · {u.plan}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-[#a0a0ab]">{u.count} gen.</span>
                              <span className={`text-sm font-medium ${u.exceeds_threshold ? 'text-[#ff6b6b]' : 'text-white'}`}>
                                {u.cost?.toFixed(4)} €
                              </span>
                              {u.exceeds_threshold && <AlertTriangle size={12} className="text-[#ff6b6b]" title="Dépasse 3× l'abonnement" />}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* OVERVIEW */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <h2 className="text-lg font-heading font-bold text-white">Vue d'ensemble</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Note moyenne', value: `${data.ratingStats?.platform_avg || 0} ⭐`, color: '#ffd93d' },
                    { label: 'Total avis', value: data.ratingStats?.total_reviews || 0, color: '#06d6a0' },
                    { label: 'Avis signalés', value: data.ratingStats?.flagged_count || 0, color: '#ff6b6b' },
                    { label: 'Posts signalés', value: data.forumQueue?.flagged?.length || 0, color: '#a855f7' },
                  ].map(stat => (
                    <div key={stat.label} className="bg-white/5 border border-white/10 rounded-2xl p-4">
                      <p className="text-xs text-[#a0a0ab] mb-1">{stat.label}</p>
                      <p className="text-2xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
                    </div>
                  ))}
                </div>
                {data.ratingStats?.low_rated_courses?.length > 0 && (
                  <div className="bg-[#ff6b6b]/5 border border-[#ff6b6b]/20 rounded-2xl p-4">
                    <p className="text-[#ff6b6b] text-sm font-medium mb-2">⚠️ Cours avec note &lt; 3 (10+ avis)</p>
                    {data.ratingStats.low_rated_courses.map(c => (
                      <div key={c._id} className="text-xs text-[#e8e8e8] flex justify-between py-1 border-b border-white/5">
                        <span>{c._id}</span>
                        <span>{c.avg.toFixed(1)} ⭐ ({c.count} avis)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* REVIEWS */}
            {activeTab === 'reviews' && (
              <div className="space-y-3">
                <h2 className="text-lg font-heading font-bold text-white">Avis signalés ({data.flaggedReviews?.length || 0})</h2>
                {!data.flaggedReviews?.length && <p className="text-[#a0a0ab] text-sm">Aucun avis signalé</p>}
                {(data.flaggedReviews || []).map(r => (
                  <div key={r.review_id} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <StarRating value={r.rating} readonly size={12} />
                        <span className="text-white text-sm font-medium">{r.user_name}</span>
                      </div>
                      <span className="text-xs bg-[#ff6b6b]/20 text-[#ff6b6b] px-2 py-0.5 rounded-full">{r.flag_count} signalement{r.flag_count > 1 ? 's' : ''}</span>
                    </div>
                    {r.title && <p className="text-white text-xs font-medium">{r.title}</p>}
                    <p className="text-[#e8e8e8] text-xs">{r.comment}</p>
                    <div className="flex gap-2">
                      <button onClick={() => handleModerateReview(r.review_id, 'hide')} className="flex items-center gap-1 text-xs bg-[#ffd93d]/10 text-[#ffd93d] px-2 py-1 rounded-lg hover:bg-[#ffd93d]/20 transition-colors">
                        <EyeOff size={10} /> Masquer
                      </button>
                      <button onClick={() => handleModerateReview(r.review_id, 'delete')} className="flex items-center gap-1 text-xs bg-[#ff6b6b]/10 text-[#ff6b6b] px-2 py-1 rounded-lg hover:bg-[#ff6b6b]/20 transition-colors">
                        <Trash2 size={10} /> Supprimer
                      </button>
                      <button onClick={() => handleModerateReview(r.review_id, 'show')} className="flex items-center gap-1 text-xs bg-[#06d6a0]/10 text-[#06d6a0] px-2 py-1 rounded-lg hover:bg-[#06d6a0]/20 transition-colors">
                        <Eye size={10} /> Valider
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ARTICLES */}
            {activeTab === 'articles' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-heading font-bold text-white">Articles ({data.articles?.length || 0})</h2>
                  <button onClick={() => navigate('/superadmin/articles/new')} className="btn-primary flex items-center gap-1.5 text-sm py-2 px-3">
                    <Plus size={12} /> Nouvel article
                  </button>
                </div>
                {!data.articles?.length && <p className="text-[#a0a0ab] text-sm">Aucun article. Créez le premier !</p>}
                {(data.articles || []).map(a => (
                  <div key={a.article_id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
                    {a.cover_image && <img src={a.cover_image} alt="" className="w-16 h-12 rounded-lg object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'published' ? 'bg-[#06d6a0]/20 text-[#06d6a0]' : 'bg-white/10 text-[#a0a0ab]'}`}>{a.status}</span>
                        {a.is_featured && <span className="text-xs text-[#ffd93d]">⭐ À la une</span>}
                      </div>
                      <p className="text-white text-sm font-medium line-clamp-1">{a.title}</p>
                      <p className="text-xs text-[#a0a0ab]">{a.category} · {a.author_name} · {a.views || 0} vues</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => navigate(`/superadmin/articles/edit/${a.article_id}`)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="Modifier">
                        <FileText size={12} className="text-[#e8e8e8]" />
                      </button>
                      <button onClick={() => handlePinArticle(a.article_id)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="Épingler">
                        <Star size={12} className="text-[#ffd93d]" />
                      </button>
                      <button onClick={() => handleDeleteArticle(a.article_id)} className="p-1.5 rounded-lg bg-[#ff6b6b]/10 hover:bg-[#ff6b6b]/20 transition-colors" title="Supprimer">
                        <Trash2 size={12} className="text-[#ff6b6b]" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* FORUM */}
            {activeTab === 'forum' && (
              <div className="space-y-3">
                <h2 className="text-lg font-heading font-bold text-white">Contenus signalés ({data.forumQueue?.length || 0})</h2>
                {!data.forumQueue?.length && <p className="text-[#a0a0ab] text-sm">Aucun contenu signalé</p>}
                {(data.forumQueue || []).map(item => (
                  <div key={item.post_id || item.reply_id} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs bg-[#a855f7]/20 text-[#a855f7] px-2 py-0.5 rounded-full">{item.type}</span>
                      <span className="text-xs text-[#ff6b6b]">{item.flag_count} signalement{item.flag_count > 1 ? 's' : ''}</span>
                    </div>
                    <p className="text-white text-sm">{item.title || item.content?.substring(0, 100)}</p>
                    <p className="text-xs text-[#a0a0ab]">par {item.author_name}</p>
                    {item.post_id && (
                      <div className="flex gap-2">
                        <button onClick={() => handleModeratePost(item.post_id, 'hide')} className="text-xs bg-[#ffd93d]/10 text-[#ffd93d] px-2 py-1 rounded-lg hover:bg-[#ffd93d]/20">Masquer</button>
                        <button onClick={() => handleModeratePost(item.post_id, 'delete')} className="text-xs bg-[#ff6b6b]/10 text-[#ff6b6b] px-2 py-1 rounded-lg hover:bg-[#ff6b6b]/20">Supprimer</button>
                        <button onClick={() => handleModeratePost(item.post_id, 'close')} className="text-xs bg-white/10 text-[#a0a0ab] px-2 py-1 rounded-lg hover:bg-white/20">Fermer</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* USERS */}
            {activeTab === 'users' && (
              <div className="space-y-3">
                <h2 className="text-lg font-heading font-bold text-white">Utilisateurs ({data.users?.length || 0})</h2>
                <div className="space-y-2">
                  {(data.users || []).map(u => (
                    <div key={u.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-3 flex-wrap">
                      <div className="w-8 h-8 rounded-full bg-[#ff6b6b]/20 flex items-center justify-center text-[#ff6b6b] text-sm font-bold flex-shrink-0">
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{u.name}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-xs text-[#a0a0ab]">{u.email}</p>
                          {u.login_method && (
                            <span className="text-xs bg-white/10 text-[#a0a0ab] px-1.5 py-0.5 rounded">
                              {LOGIN_METHOD_LABELS[u.login_method] || u.login_method}
                            </span>
                          )}
                          {u.kurso_role && u.kurso_role !== 'standard' && (
                            <span className="text-xs bg-[#ff6b6b]/20 text-[#ff6b6b] px-1.5 py-0.5 rounded font-medium">
                              {KURSO_ROLES.find(r => r.value === u.kurso_role)?.label}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <select value={u.role} onChange={e => handleUserRole(u.id, e.target.value)}
                          data-testid={`user-role-${u.id}`}
                          className="text-xs bg-white/5 border border-white/10 text-white rounded-lg px-2 py-1">
                          <option value="user">Utilisateur</option>
                          <option value="creator">Créateur</option>
                          <option value="admin">Admin</option>
                        </select>
                        <select value={u.community_role || 'user'} onChange={e => handleCommunityRole(u.id, e.target.value)}
                          className="text-xs bg-white/5 border border-white/10 text-white rounded-lg px-2 py-1">
                          <option value="user">Standard</option>
                          <option value="moderator">Modérateur</option>
                          <option value="expert">Expert</option>
                        </select>
                        <select value={u.kurso_role || 'standard'} onChange={e => handleKursoRole(u.id, e.target.value)}
                          data-testid={`kurso-role-${u.id}`}
                          className="text-xs bg-[#ff6b6b]/10 border border-[#ff6b6b]/20 text-[#ff6b6b] rounded-lg px-2 py-1">
                          {KURSO_ROLES.map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AUDIT LOG */}
            {activeTab === 'audit' && (
              <div className="space-y-4">
                <h2 className="text-lg font-heading font-bold text-white">Journal d'audit</h2>
                <p className="text-xs text-[#a0a0ab]">Toutes les actions d'assignation de rôle équipe sont enregistrées ici.</p>
                <div className="space-y-2">
                  {(data.auditLog || []).length === 0 && (
                    <p className="text-[#a0a0ab] text-sm">Aucune entrée dans le journal.</p>
                  )}
                  {(data.auditLog || []).map((entry, i) => (
                    <div key={`${entry.timestamp}-${entry.target_email}-${i}`} className="bg-white/5 border border-white/10 rounded-xl p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[#ff6b6b] font-medium">{entry.action}</span>
                        <span className="text-[#a0a0ab] text-xs">{new Date(entry.timestamp).toLocaleString('fr-FR')}</span>
                      </div>
                      <p className="text-[#e8e8e8] text-xs">
                        <span className="text-[#a0a0ab]">Par :</span> {entry.performed_by_email} →
                        <span className="text-[#a0a0ab]"> Cible :</span> {entry.target_email} —
                        <span className="text-[#a0a0ab]"> Rôle :</span> <span className="line-through opacity-50">{entry.old_role}</span> → <span className="text-[#06d6a0]">{entry.new_role}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SOCIAL / PARAMETRES */}
            {activeTab === 'social' && (
              <div className="space-y-6 max-w-lg">
                <div>
                  <h2 className="text-lg font-heading font-bold text-white mb-1">Réseaux sociaux officiels</h2>
                  <p className="text-xs text-[#a0a0ab] mb-4">Ces liens apparaissent dans le footer et les boutons de partage. Laissez vide pour masquer.</p>
                  {[
                    { key: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/kurso' },
                    { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/kurso' },
                    { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/company/kurso' },
                    { key: 'tiktok', label: 'TikTok', icon: Settings, placeholder: 'https://tiktok.com/@kurso' },
                    { key: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@kurso' },
                    { key: 'twitter', label: 'X (Twitter)', icon: Twitter, placeholder: 'https://x.com/kurso' },
                  ].map(({ key, label, icon: Icon, placeholder }) => (
                    <div key={key} className="mb-3">
                      <label className="flex items-center gap-2 text-xs text-[#a0a0ab] mb-1.5"><Icon size={12} /> {label}</label>
                      <input type="url" value={socialForm[key] || ''} onChange={e => setSocialForm(f => ({ ...f, [key]: e.target.value }))}
                        placeholder={placeholder}
                        className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                        data-testid={`social-${key}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 pt-5">
                  <h3 className="text-white font-semibold text-sm mb-1 flex items-center gap-2">
                    <MessageSquare size={14} className="text-[#20c997]" /> Chat Crisp
                  </h3>
                  <p className="text-xs text-[#a0a0ab] mb-3">Entrez votre Website ID Crisp pour activer le chat en direct sur tout le site.</p>
                  <input
                    type="text" value={crispId} onChange={e => setCrispId(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    data-testid="crisp-website-id-input"
                    className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#20c997]/50 mb-1"
                  />
                  <p className="text-xs text-[#a0a0ab]">
                    Disponible sur <a href="https://app.crisp.chat" target="_blank" rel="noopener noreferrer" className="text-[#20c997] hover:underline">app.crisp.chat</a> → Paramètres → Website Settings → Website ID
                  </p>
                </div>
                <button onClick={handleSaveSocial} className="btn-primary w-full py-3 text-sm" data-testid="save-social-btn">
                  Sauvegarder les paramètres
                </button>
              </div>
            )}
          </>
        )}

        <Footer />
      </div>
    </div>
  );
}
